import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useCurrentUser } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useNewsletters, useCreateNewsletter, useSubmitContribution } from '../hooks/useNewsletters';
import { Newsletter } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import toast from 'react-hot-toast';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { 
  Newspaper, 
  Plus, 
  Eye, 
  Download, 
  Calendar, 
  User, 
  ArrowRight,
  ChevronRight,
  TrendingUp,
  Mail,
  FileText,
  Upload,
  Heart,
  MessageCircle,
  CheckCircle2,
  Image as ImageIcon,
  Layout
} from 'lucide-react';
import { cn, safeFormat } from '../lib/utils';
import DOMPurify from 'dompurify';
import { Contribution, ContributionStatus, TextSection, HeroSection, ImageSection, StorySection, NoticeSection, ButtonSection, EventEmbedSection } from '../types';
import { useContributions, useApproveContribution, useRejectContribution } from '../hooks/useNewsletters';

const NewsletterReader: React.FC<{ newsletter: Newsletter; isOpen: boolean; onClose: () => void }> = ({ newsletter, isOpen, onClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={newsletter.title}
      className="max-w-4xl h-[90vh] overflow-hidden flex flex-col p-0"
    >
      <div className="flex-1 overflow-y-auto bg-[#fcfcfc] dark:bg-slate-900">
        {newsletter.sections && newsletter.sections.length > 0 ? (
          <div className="pb-20">
            {newsletter.sections.map((section, idx) => (
              <div key={section.id || idx}>
                {section.type === 'hero' && (
                  <div className="relative min-h-[360px] md:min-h-[440px] flex items-center justify-center p-8 md:p-12 text-center text-white overflow-hidden">
                    {section.imageUrl && (
                      <img 
                        src={section.imageUrl} 
                        alt={section.title || 'Newsletter hero'} 
                        className="absolute inset-0 w-full h-full object-cover -z-10" 
                        referrerPolicy="no-referrer" 
                      />
                    )}
                    <div className="absolute inset-0 bg-navy-950/70 -z-10" />
                    <div className="max-w-2xl px-4">
                      <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 leading-tight tracking-tight">{section.title}</h2>
                      {section.subtitle && (
                        <p className="text-base sm:text-lg text-white/90 font-mono uppercase tracking-widest mb-6">{section.subtitle}</p>
                      )}
                      {section.ctaText && (
                        <Button 
                          variant="primary" 
                          className="bg-white text-navy-950 hover:bg-gray-100 border-none px-8 py-3 rounded-none font-bold shadow-[4px_4px_0px_#000000] dark:bg-white dark:text-black"
                          onClick={() => section.ctaUrl && window.open(section.ctaUrl, '_blank')}
                        >
                          {section.ctaText}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {section.type === 'text' && (
                  <div className="p-8 md:p-16 max-w-3xl mx-auto">
                    {section.title && (
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">{section.title}</h2>
                    )}
                    <div 
                      className="prose prose-lg text-gray-700 dark:text-gray-300 leading-[1.8] font-serif max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(
                          (section as TextSection).htmlContent || 
                          section.content || 
                          ''
                        ) 
                      }}
                    />
                  </div>
                )}

                {section.type === 'notice' && (
                  <div className="px-6 md:px-12 py-8 max-w-3xl mx-auto">
                    <div className={cn(
                      "border-l-[6px] p-6 md:p-8 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] border-2 border-black dark:border-white",
                      section.severity === 'warning' ? "bg-amber-50 border-l-amber-500" :
                      section.severity === 'error' ? "bg-red-50 border-l-red-500" :
                      "bg-blue-50 border-l-blue-600"
                    )}>
                      <h3 className="text-navy-950 dark:text-navy-950 font-bold text-lg mb-2 uppercase tracking-tight flex items-center gap-2">
                        {section.severity === 'warning' ? <CheckCircle2 size={18} className="text-amber-600" /> : <Plus size={18} className="text-blue-600" />}
                        {section.title}
                      </h3>
                      <p className="text-gray-800 text-sm md:text-base leading-relaxed font-serif">{section.content}</p>
                    </div>
                  </div>
                )}

                {section.type === 'image' && (
                  <div className="px-6 md:px-12 py-8 max-w-3xl mx-auto">
                    <img 
                      src={section.imageUrl} 
                      alt={section.caption || 'Newsletter section image'} 
                      className="w-full rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] mb-3" 
                      referrerPolicy="no-referrer" 
                    />
                    {(section.caption || section.content) && (
                      <p className="text-center text-xs text-gray-500 dark:text-gray-400 font-mono uppercase tracking-widest">{section.caption || section.content}</p>
                    )}
                  </div>
                )}

                {section.type === 'story' && (
                  <div className="px-6 md:px-12 py-10 max-w-3xl mx-auto">
                    <div className="bg-white border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none p-8 md:p-12 flex flex-col items-center text-center dark:bg-slate-900">
                      <div className="w-16 h-1 bg-brand-primary mb-6" />
                      <h3 className="text-2xl font-black text-gray-900 dark:text-gray-100 mb-4">{section.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300 italic font-serif text-lg leading-relaxed mb-6">"{section.content}"</p>
                      {section.author && (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-none bg-navy-100 flex items-center justify-center text-navy-600 font-bold text-xs border border-black dark:border-white">
                            {section.author.charAt(0)}
                          </div>
                          <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">{section.author}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {section.type === 'button' && (
                  <div className="py-8 text-center">
                    <Button 
                      variant="primary"
                      className="px-8 py-3 rounded-none font-bold uppercase tracking-wider shadow-[4px_4px_0px_#000000]"
                      onClick={() => (section as ButtonSection).url && window.open((section as ButtonSection).url, '_blank')}
                    >
                      {(section as ButtonSection).text}
                    </Button>
                  </div>
                )}

                {section.type === 'divider' && (
                  <div className="py-8 max-w-3xl mx-auto px-6">
                    <hr className="border-t-2 border-black dark:border-white" />
                  </div>
                )}

                {section.type === 'event_embed' && (
                  <div className="px-6 md:px-12 py-8 max-w-3xl mx-auto">
                    <div className="bg-white border-2 border-black dark:border-white p-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] rounded-none dark:bg-slate-900">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="blue" className="text-[10px] uppercase font-mono tracking-widest">
                          Featured Event
                        </Badge>
                      </div>
                      <h3 className="text-xl font-black text-navy-900 dark:text-white mb-2">
                        {(section as any).title || `Event #${(section as any).eventId || 'Scheduled Event'}`}
                      </h3>
                      {(section as any).content && (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {(section as any).content}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 md:p-20 max-w-2xl mx-auto">
            <h2 className="text-3xl font-black text-navy-900 dark:text-white mb-8">{newsletter.title}</h2>
            <div className="prose prose-lg text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
              {newsletter.content}
            </div>
            {newsletter.pdfUrl && (
              <Button className="mt-8 gap-2" variant="secondary" onClick={() => window.open(newsletter.pdfUrl, '_blank')}>
                <Download size={18} />
                Download PDF Version
              </Button>
            )}
          </div>
        )}

        <div className="p-12 bg-navy-900 text-white text-center">
          <p className="text-[10px] text-navy-500 uppercase tracking-widest">End of Issue</p>
        </div>
      </div>
    </Modal>
  );
};

const ContributionAggregator: React.FC<{ nodeId: string }> = ({ nodeId }) => {
  const { data: contributions = [], isLoading } = useContributions(nodeId);
  const approveMutation = useApproveContribution(nodeId);
  const rejectMutation = useRejectContribution(nodeId);

  const pendingContributions = contributions.filter((c: any) => c.status === ContributionStatus.PENDING);

  if (isLoading) return null;

  return (
    <Card className="bg-navy-900 text-white border-none mb-8 shadow-2xl">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-primary rounded-none text-navy-900 dark:text-white">
            <Mail size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold">Contribution Aggregator</h2>
            <p className="text-xs text-navy-200">Review member-submitted content for the next issue</p>
          </div>
        </div>
        <Badge variant="blue" className="bg-navy-800 text-brand-primary border-none">
          {pendingContributions.length} Pending
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pendingContributions.map((contribution: any) => (
          <Card key={contribution.id} className="bg-navy-800 border-navy-700 hover:border-navy-600 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-none bg-navy-700 flex items-center justify-center text-xs font-bold text-brand-primary">
                {(contribution.authorName || 'M')[0]}
              </div>
              <div>
                <p className="text-xs font-bold text-white leading-none">{contribution.authorName || 'Member'}</p>
                <p className="text-[10px] text-navy-300 font-mono">
                  {safeFormat(contribution.createdAt, 'MMM d')}
                </p>
              </div>
            </div>
            
            {contribution.imageUrl && (
              <div className="aspect-video rounded-none overflow-hidden mb-3 relative group">
                <img 
                  src={contribution.imageUrl} 
                  alt="Submission"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Badge variant="blue">View Photo</Badge>
                </div>
              </div>
            )}
            
            <p className="text-xs text-navy-100 line-clamp-3 mb-4 italic leading-relaxed">
              "{contribution.content}"
            </p>

            <div className="flex items-center gap-2 mt-auto">
              <Button 
                size="sm" 
                variant="secondary" 
                className="flex-1 py-1.5 h-8 text-[10px] bg-navy-700 hover:bg-navy-600 text-white border-navy-600 hover:border-brand-primary"
                onClick={() => rejectMutation.mutate(contribution.id)}
                isLoading={rejectMutation.isPending && rejectMutation.variables === contribution.id}
              >
                Reject
              </Button>
              <Button 
                size="sm" 
                className="flex-1 py-1.5 h-8 text-[10px] bg-brand-primary hover:bg-brand-primary/90 text-navy-900 dark:text-white border-none font-bold"
                onClick={() => approveMutation.mutate(contribution.id)}
                isLoading={approveMutation.isPending && approveMutation.variables === contribution.id}
              >
                Approve
              </Button>
            </div>
          </Card>
        ))}
        {pendingContributions.length === 0 && (
          <div className="col-span-full py-8 text-center text-navy-400 text-sm italic">
            Waiting for more member stories...
          </div>
        )}
      </div>
    </Card>
  );
};

const NewsletterCard: React.FC<{ newsletter: Newsletter; onRead: (n: Newsletter) => void }> = ({ newsletter, onRead }) => {
  const { node } = useTenant();
  const navigate = useNavigate();
  const isFromParent = newsletter.nodeId !== node?.id;
  const terms = node?.config?.terminology;
  const isDraft = newsletter.status === 'DRAFT';

  return (
    <Card className={cn(
      "group overflow-hidden transition-all hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] hover:-translate-y-1 bg-white border-black dark:border-white dark:bg-slate-900",
      isFromParent && "border-l-4 border-l-navy-600"
    )}>
      <div className="aspect-[4/5] bg-white relative overflow-hidden dark:bg-slate-800">
        {newsletter.thumbnailUrl ? (
          <img 
            src={newsletter.thumbnailUrl} 
            alt={newsletter.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <Newspaper size={48} />
          </div>
        )}
        
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {isFromParent && (
            <Badge variant="blue" className="bg-navy-900 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] text-[9px] uppercase tracking-widest px-2 py-1">
              {terms?.parentNode || 'From'}: {newsletter.nodeName}
            </Badge>
          )}
          {isDraft && (
            <Badge variant="amber" className="shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] text-[9px] uppercase tracking-widest px-2 py-1">
              Draft
            </Badge>
          )}
          {newsletter.status === 'PUBLISHING' && (
            <Badge variant="blue" className="shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] text-[9px] uppercase tracking-widest px-2 py-1 flex items-center gap-1">
              <Spinner size="sm" />
              Broadcasting
            </Badge>
          )}
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-opacity">
          {isDraft ? (
            <Button 
              variant="primary" 
              size="sm" 
              className="w-full gap-2 backdrop-blur-sm bg-blue-600/80 hover:bg-blue-600 border-white/30"
              onClick={() => navigate(`/newsletter/studio/${newsletter.id}`)}
            >
              <Layout size={16} />
              Open Studio
            </Button>
          ) : (
            <Button 
              variant="primary" 
              size="sm" 
              className="w-full gap-2 backdrop-blur-sm bg-white/20 hover:bg-white/30 border-white/30 dark:bg-slate-900"
              onClick={() => onRead(newsletter)}
            >
              <Eye size={16} />
              Read Issue
            </Button>
          )}
        </div>
      </div>

      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest font-mono">
            {newsletter.issueNumber || 'COMMUNICATION'}
          </span>
          {!isDraft && (
            <div className="flex items-center gap-1 text-[10px] text-gray-400 font-mono">
              <TrendingUp size={12} />
              <span>{newsletter.metrics?.recipientsTotal || 0} Delivered</span>
            </div>
          )}
        </div>

        <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug group-hover:text-blue-700 transition-colors">
          {newsletter.title || 'Untitled Newsletter'}
        </h3>

        <div className="flex items-center justify-between pt-3 border-t border-gray-50">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-none bg-navy-50 flex items-center justify-center text-[10px] font-bold text-navy-600">
              {newsletter.authorName?.[0] || 'A'}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{newsletter.authorName || 'Anonymous'}</span>
          </div>
          <span className="text-[10px] text-gray-400 font-mono">
            {safeFormat(newsletter.publishedAt || newsletter.createdAt, 'MMM d, yyyy')}
          </span>
        </div>
      </div>
    </Card>
  );
};

export const NewsletterPage = () => {
  const { node, isGlobal } = useTenant();
  const { user } = useCurrentUser();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [readingNewsletter, setReadingNewsletter] = useState<Newsletter | null>(null);
  
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const { data: newsletters = [], isLoading } = useNewsletters(hasActiveNode ? (node?.id || '') : '');
  const createMutation = useCreateNewsletter(hasActiveNode ? (node?.id || '') : '');
  const submitMutation = useSubmitContribution(hasActiveNode ? (node?.id || '') : '');

  const handleCreate = async () => {
    if (!hasActiveNode) {
      toast.error('Active organization node required to draft newsletters');
      return;
    }
    createMutation.mutate(undefined, {
      onSuccess: (data) => {
        navigate(`/newsletter/studio/${data.id}`);
      }
    });
  };

  const handleSubmitStory = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!hasActiveNode) {
      toast.error('Active organization node required to submit stories');
      return;
    }
    const formData = new FormData(e.currentTarget);
    
    submitMutation.mutate({
      title: formData.get('title') as string,
      content: formData.get('content') as string,
      imageUrl: formData.get('imageUrl') as string || undefined,
      authorName: user?.fullName || (window as any).Clerk?.user?.fullName || 'Anonymous'
    }, {
      onSuccess: () => setIsSubmitOpen(false)
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="lg" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 font-medium">Synchronizing communications...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-black dark:border-white pb-8">
        <div className="space-y-2">
          <h1 className="text-2xl md:text-4xl font-black text-navy-900 dark:text-white tracking-tight">The Newsstand</h1>
          <p className="text-sm md:text-lg text-gray-500 dark:text-gray-400 max-w-xl">
            Official organizational dispatches, success stories, and member spotlight narratives.
          </p>
        </div>
        
        {hasActiveNode && (
          <div className="flex gap-4">
            <Button 
              variant="secondary" 
              className="rounded-none px-6 border-black dark:border-white"
              onClick={() => setIsSubmitOpen(true)}
            >
              <Upload size={18} className="mr-2" />
              Submit Story
            </Button>
            {can('create_newsletter') && (
              node?.tier === 'LOCAL' ? (
                <Button 
                  variant="primary" 
                  disabled
                  className="rounded-none px-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-blue-500/20 opacity-50 cursor-not-allowed"
                  title="Upgrade from Free tier to unlock Newsletters"
                >
                  <Plus size={20} className="mr-2" />
                  Upgrade to Unlock
                </Button>
              ) : (
                <Button 
                  variant="primary" 
                  onClick={handleCreate} 
                  isLoading={createMutation.isPending}
                  className="rounded-none px-6 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-blue-500/20"
                >
                  <Plus size={20} className="mr-2" />
                  New Draft
                </Button>
              )
            )}
          </div>
        )}
      </div>

      {!hasActiveNode ? (
        <div className="border-8 border-black dark:border-white p-8 md:p-12 bg-white dark:bg-black shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#FFFFFF] text-center space-y-4 max-w-xl mx-auto my-12 rounded-none">
          <div className="w-16 h-16 bg-[#FFD600] border-4 border-black dark:border-white flex items-center justify-center mx-auto text-black rounded-none">
            <Newspaper size={32} />
          </div>
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-black dark:text-white font-display">Organization Required</h2>
          <p className="text-sm font-bold text-gray-600 dark:text-gray-400 leading-relaxed">
            The Newsstand and newsletter publishing belong to specific organization nodes. Please switch to an active organization to view or draft newsletters.
          </p>
          <div className="pt-4">
            <Button variant="primary" onClick={() => navigate('/dashboard')} className="rounded-none px-8 py-3 bg-black text-white dark:bg-white dark:text-black font-bold uppercase tracking-wider shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
              Go to Dashboard
            </Button>
          </div>
        </div>
      ) : (
        <>
          {!can('create_newsletter') && (
            <Card className="bg-brand-primary/5 border-dashed border-2 border-brand-primary/30 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-brand-primary rounded-none text-navy-900 dark:text-white">
                  <ImageIcon size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-navy-900 dark:text-white">Share your picnic photos!</h3>
                  <p className="text-sm text-gray-600">We're collecting content for the Summer 2024 Newsletter. Submit your favorite moments from the recent chapter picnic.</p>
                </div>
                <Button size="sm" className="hidden md:flex" onClick={() => setIsSubmitOpen(true)}>Upload Content</Button>
              </div>
            </Card>
          )}

          {can('create_newsletter') && node && (
            <ContributionAggregator nodeId={node.id} />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {newsletters.map((newsletter: any) => (
              <NewsletterCard key={newsletter.id} newsletter={newsletter} onRead={setReadingNewsletter} />
            ))}

            {newsletters.length === 0 && (
              <div className="col-span-full py-20 bg-white rounded-none flex flex-col items-center justify-center text-center border-2 border-dashed border-black dark:border-white dark:bg-slate-950">
                <Newspaper size={48} className="text-gray-300 mb-4" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">No issues published yet</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Check back soon for latest organizational newsletters.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {readingNewsletter && (
        <NewsletterReader 
          newsletter={readingNewsletter} 
          isOpen={!!readingNewsletter} 
          onClose={() => setReadingNewsletter(null)} 
        />
      )}

      <Modal
        isOpen={isSubmitOpen}
        onClose={() => setIsSubmitOpen(false)}
        title="Submit Content for Newsletter"
        className="max-w-xl"
      >
        <form onSubmit={handleSubmitStory} className="space-y-4">
          <div className="p-4 bg-blue-50 border-2 border-black dark:border-white border-blue-100 rounded-none mb-4">
            <p className="text-xs text-blue-700 leading-relaxed font-medium">
              Your submission will be reviewed by administrators before being added to the organization's content library.
            </p>
          </div>

          <Input 
            label="Story Title / Photo Caption" 
            name="title" 
            placeholder="e.g. Highlights from the Spring Picnic" 
            required 
          />
          
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">Story / Content</label>
            <textarea 
              name="content" 
              className="w-full px-4 py-3 text-sm border-2 border-black dark:border-white rounded-none focus:outline-none focus:border-blue-500 bg-white h-32 dark:bg-slate-950"
              placeholder="Describe your photo or write your story here..."
              required
            />
          </div>

          <Input 
            label="Image URL (Optional)" 
            name="imageUrl" 
            placeholder="https://images.unsplash.com/..." 
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" type="button" onClick={() => setIsSubmitOpen(false)}>Cancel</Button>
            <Button variant="primary" type="submit" isLoading={submitMutation.isPending}>
              Submit for Review
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
